import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useStore } from '@nanostores/react';
import { urlFor } from '@agentic/magento-client/lib/url-for';
import { applyVatPreference } from '@agentic/magento-client/lib/vat-prefs';
import { b2bFlagsStore, requestB2bFlags } from '@agentic/magento-client/lib/b2b-product-flags';
import { shouldShowQuoteButton } from '@agentic/magento-client/lib/feature-flags';
import { addToQuote, isQuoteDrawerOpen } from '@agentic/magento-client/stores/quote';

interface StickyBuyBarProps {
    productName: string;
    productSku: string;
    price: number;
    regularPrice?: number;
    currency: string;
    imageUrl?: string;
    stockStatus?: string;
    urlKey?: string;
    /** Product tax-class id → per-product VAT rate (store rate if absent). */
    taxClassId?: number | null;
    hidePurchase?: boolean;
    /** Landing pages return to the real buy box so required options cannot be bypassed. */
    purchaseAnchor?: string;
}

export default function StickyBuyBar({
    productName,
    productSku,
    price,
    regularPrice,
    currency,
    imageUrl,
    stockStatus = 'IN_STOCK',
    urlKey,
    taxClassId,
    hidePurchase = false,
    purchaseAnchor,
}: StickyBuyBarProps) {
    const [isVisible, setIsVisible] = useState(false);
    const [qty, setQty] = useState(1);
    const [adding, setAdding] = useState(false);
    const [added, setAdded] = useState(false);

    const isInStock = stockStatus === 'IN_STOCK';
    const hasDiscount = regularPrice && regularPrice > price;

    // B2B Suite hide-price / quote (mirror AddToCart.tsx)
    const b2bFlagsMap = useStore(b2bFlagsStore);
    useEffect(() => { requestB2bFlags([productSku]); }, [productSku]);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    useEffect(() => {
        try { setIsLoggedIn(!!localStorage.getItem('customer_token')); } catch { /* SSR / blocked */ }
        const onAuth = (e: Event) => setIsLoggedIn(!!(e as CustomEvent<{ isLoggedIn?: boolean }>).detail?.isLoggedIn);
        window.addEventListener('auth-change', onAuth);
        return () => window.removeEventListener('auth-change', onAuth);
    }, []);
    const sbFlags = productSku ? b2bFlagsMap[productSku] : undefined;
    const hideMode = sbFlags?.hidePrice ? (sbFlags.hidePriceMode ?? 'hide_all') : null;
    const cartHidden = hideMode != null && hideMode !== 'hide_price_only';
    const priceHidden = hideMode != null && hideMode !== 'quote_only';
    const forceQuote = hideMode === 'replace_with_quote' || hideMode === 'quote_only';
    const showQuote = !!productSku && (
        forceQuote || (shouldShowQuoteButton('pdp', isLoggedIn) && (sbFlags?.quoteAllowed ?? false))
    );

    // Show sticky bar ONLY when the buy-box (with its primary ADD TO
    // BASKET button) is fully out of view. Previously we keyed off the
    // description/tabs section entering the viewport, which fired while the
    // buy-box was still visible — producing a double-CTA (sticky "ADD TO
    // CART" stacked on top of the main "ADD TO BASKET") that looked broken
    // and confused the user with two different CTAs competing for the same
    // action. The fix: observe the primary add-to-cart button itself and
    // flip visibility when its bottom edge has crossed above the viewport.
    useEffect(() => {
        if (hidePurchase) return;
        if (purchaseAnchor) {
            // Query the current control after responsive layout/hydration changes.
            // Observing a replaced button would leave the shortcut hidden.
            const update = () => {
                const cta = document.querySelector('[data-pdp-primary-cta]') ?? document.querySelector('#col-pcr-buybox');
                setIsVisible(!!cta && cta.getBoundingClientRect().bottom < 0);
            };
            window.addEventListener('scroll', update, { passive: true });
            window.addEventListener('resize', update);
            update();
            return () => {
                window.removeEventListener('scroll', update);
                window.removeEventListener('resize', update);
            };
        }
        // Primary target — the actual ADD TO BASKET button in the buy-box.
        const primaryCta =
            document.querySelector('[data-pdp-primary-cta]') ??
            document.querySelector('[data-block-type="add_to_cart"] button') ??
            document.querySelector('#col-pcr-buybox button[type="submit"]') ??
            document.querySelector('#col-pcr-options button') ??
            document.querySelector('[data-block-type="product_options"] button');

        // Fallback target — the buy-box column itself.
        const buyBox = document.querySelector(
            '#col-pcr-buybox, #col-pcr-options, [data-block-type="product_options"]'
        );

        const target = primaryCta ?? buyBox;

        if (target && 'IntersectionObserver' in window) {
            // Show the sticky bar only once the primary CTA is fully above
            // the viewport (its bottom edge < 0). While any part of the main
            // button is still on screen, keep the bar hidden.
            const observer = new IntersectionObserver(
                ([entry]) => {
                    const bottom = entry.boundingClientRect.bottom;
                    setIsVisible(!entry.isIntersecting && bottom < 0);
                },
                { threshold: 0 }
            );
            observer.observe(target);
            return () => observer.disconnect();
        }

        // Legacy fallback — pure scroll-based, still keyed to buy-box bottom.
        const handleScroll = () => {
            if (buyBox) {
                const rect = buyBox.getBoundingClientRect();
                setIsVisible(rect.bottom < 0);
            } else {
                setIsVisible(window.scrollY > 600);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, [hidePurchase, purchaseAnchor]);

    // Add to cart handler — dispatches CustomEvent (same as AddToCart block)
    const handleAddToCart = useCallback(() => {
        if (!isInStock || adding) return;
        setAdding(true);

        // Dispatch the same CustomEvent that cart.ts initCart() listens for
        window.dispatchEvent(new CustomEvent('add-to-cart', {
            detail: {
                sku: productSku,
                qty,
                name: productName,
                price,
                image: imageUrl,
                urlKey,
            }
        }));

        // Show feedback
        setTimeout(() => {
            setAdding(false);
            setAdded(true);
            setTimeout(() => setAdded(false), 2000);
        }, 600);
    }, [productSku, qty, isInStock, adding, productName, price, imageUrl, urlKey]);

    // Add to quote handler (B2B Suite)
    const handleAddToQuote = useCallback(() => {
        if (!productSku) return;
        addToQuote({ id: String(productSku), sku: productSku, name: productName, price, image: imageUrl ?? '', urlKey: urlKey ?? '' }, qty);
        isQuoteDrawerOpen.set(true);
    }, [productSku, productName, price, imageUrl, urlKey, qty]);

    // Format price
    // applyVatPreference honours Magento's tax config (rate + ex/inc display) so
    // the sticky bar price matches the main PDP price instead of always showing
    // the raw ex-VAT figure.
    const formatPrice = (val: number) =>
        new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(applyVatPreference(val, undefined, taxClassId));

    const stickyBarContent = isVisible && purchaseAnchor ? (
        <div className="ae-sales-sticky sticky-buy-bar-enter fixed bottom-0 lg:bottom-auto lg:top-0 left-0 right-0 z-[60] border-y border-[var(--glass-border)]" style={{ background: 'var(--surface-1)', color: 'var(--text-primary)', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}>
            <div className="max-w-[1400px] mx-auto px-4 lg:px-6 flex items-center justify-between gap-5 min-h-[64px] py-3">
                <span className="hidden md:block truncate text-sm font-semibold">{productName}</span>
                <a href={purchaseAnchor} className="w-full md:w-auto text-center px-6 py-3 text-sm font-bold" style={{ background: 'var(--style-cta-primary-bg, #15803d)', color: 'var(--style-cta-primary-text, #fff)', borderRadius: 'var(--style-btn-radius, 8px)' }}>View purchase options ↗</a>
            </div>
        </div>
    ) : isVisible ? (
        <div
            className="sticky-buy-bar-enter fixed bottom-0 lg:bottom-auto lg:top-0 left-0 right-0 z-[60] border-t lg:border-t-0 border-[var(--glass-border,rgba(255,255,255,0.08))] lg:border-b"
            style={{
                backgroundColor: 'var(--style-header-strip-bg, var(--surface-1, #0a0a0a))',
                backdropFilter: 'var(--style-backdrop-filter, blur(20px))',
                WebkitBackdropFilter: 'var(--style-backdrop-filter, blur(20px))',
                boxShadow: 'var(--style-header-scrolled-shadow, 0 -4px 24px rgba(0,0,0,0.3))',
            }}
        >
            <div className="max-w-[1400px] mx-auto px-4 lg:px-6 flex items-center justify-between lg:justify-start gap-3 lg:gap-5 h-[60px] lg:h-[70px]">
                {/* Product thumbnail - hidden on mobile */}
                {imageUrl && (
                    <a href={urlFor('product', urlKey)} className="hidden md:block flex-shrink-0">
                        <img
                            src={imageUrl}
                            alt={productName}
                            className="w-10 h-10 lg:w-12 lg:h-12 object-contain bg-[var(--surface-1)]"
                            style={{ borderRadius: 'var(--style-img-radius, 8px)', border: '1px solid var(--glass-border)' }}
                        />
                    </a>
                )}

                {/* Product name + stock - hidden on mobile */}
                <div className="hidden md:flex flex-1 min-w-0 items-center gap-3">
                    <h3
                        className="text-sm font-semibold truncate"
                        style={{ color: 'var(--color-sticky-bar-text, #f1f5f9)', maxWidth: '360px' }}
                    >
                        {productName}
                    </h3>
                    {isInStock ? (
                        <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider flex-shrink-0"
                            style={{ color: 'var(--color-sticky-bar-instock, #4ade80)' }}>
                            <span style={{ fontSize: '8px' }}>●</span> IN STOCK
                        </span>
                    ) : (
                        <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider flex-shrink-0"
                            style={{ color: 'var(--color-sticky-bar-outstock, #f87171)' }}>
                            <span style={{ fontSize: '8px' }}>●</span> OUT OF STOCK
                        </span>
                    )}
                </div>

                {/* Price — replaced by the merchant hide-price text when hidden;
                    quote_only keeps the real price. */}
                <div className="flex flex-col lg:flex-row lg:items-center gap-0 lg:gap-2 flex-shrink-0">
                    {priceHidden ? (
                        <span className="text-base lg:text-lg font-bold" style={{ color: 'var(--color-sticky-bar-accent, var(--color-secondary, #FB7C00))' }}>
                            {sbFlags?.hidePriceText || 'Request a Quote'}
                        </span>
                    ) : (
                        <>
                            {hasDiscount && (
                                <span className="text-[10px] lg:text-sm line-through" style={{ color: 'var(--color-sticky-bar-muted, #94a3b8)' }}>
                                    {formatPrice(regularPrice)}
                                </span>
                            )}
                            <div className="flex items-baseline gap-1">
                                <span className="text-base lg:text-lg font-bold" style={{ color: 'var(--color-sticky-bar-accent, var(--color-secondary, #FB7C00))' }}>
                                    {formatPrice(price)}
                                </span>
                                <span className="text-[9px] lg:text-[10px] uppercase tracking-wider" style={{ color: 'var(--color-sticky-bar-muted, #94a3b8)' }}>
                                    EX. VAT
                                </span>
                            </div>
                        </>
                    )}
                </div>

                {/* Desktop Qty selector - hidden on mobile to save space. Hidden
                    entirely when there's no buy/quote action (pure hide mode). */}
                {(!cartHidden || showQuote) && (
                <div className="hidden lg:flex items-center gap-0 flex-shrink-0 overflow-hidden lg:ml-auto"
                    style={{ border: '1px solid var(--color-sticky-bar-border, rgba(255,255,255,0.22))', borderRadius: 'var(--style-btn-radius, 8px)' }}>
                    <button
                        onClick={() => setQty(Math.max(1, qty - 1))}
                        className="w-8 h-9 flex items-center justify-center text-sm font-bold transition-colors hover:bg-[color-mix(in_srgb,var(--surface-1)_5%,transparent)]"
                        style={{ color: 'var(--color-sticky-bar-text, #f1f5f9)' }}
                    >
                        −
                    </button>
                    <span className="w-8 h-9 flex items-center justify-center text-sm font-semibold"
                        style={{ color: 'var(--color-sticky-bar-text, #f1f5f9)', borderLeft: '1px solid var(--color-sticky-bar-border, rgba(255,255,255,0.22))', borderRight: '1px solid var(--color-sticky-bar-border, rgba(255,255,255,0.22))' }}>
                        {qty}
                    </span>
                    <button
                        onClick={() => setQty(qty + 1)}
                        className="w-8 h-9 flex items-center justify-center text-sm font-bold transition-colors hover:bg-[color-mix(in_srgb,var(--surface-1)_5%,transparent)]"
                        style={{ color: 'var(--color-sticky-bar-text, #f1f5f9)' }}
                    >
                        +
                    </button>
                </div>
                )}

                {/* CTA — Add to Basket normally; Add to Quote when the buy button
                    is hidden by a B2B mode (quote_only / replace_with_quote / etc).
                    Pure hide modes with no quote eligibility show no CTA. */}
                {!cartHidden ? (
                <button
                    onClick={handleAddToCart}
                    disabled={!isInStock || adding}
                    className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-4 lg:px-6 py-2 lg:py-2.5 text-[13px] lg:text-sm font-bold uppercase tracking-wider transition-all duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                        borderRadius: 'var(--style-btn-radius, 8px)',
                        // Match the main buy-box ADD TO BASKET button exactly: same
                        // primary-CTA token + green default (#15803d), so the sticky
                        // bar's CTA is the same green, not orange. (AddToCart.tsx uses
                        // var(--style-cta-primary-bg, #15803d) / -text.)
                        background: added
                            ? 'var(--color-success, linear-gradient(135deg, #22c55e, #16a34a))'
                            : 'var(--style-cta-primary-bg, #15803d)',
                        color: added
                            ? 'var(--text-on-accent, #ffffff)'
                            : 'var(--style-cta-primary-text, #ffffff)',
                        boxShadow: added
                            ? '0 0 20px color-mix(in srgb, var(--color-success, #22c55e) 30%, transparent)'
                            : '0 0 20px color-mix(in srgb, var(--style-cta-primary-bg, #15803d) 30%, transparent)',
                    }}
                >
                    {/* Cart icon */}
                    <svg className="w-4 h-4 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
                    </svg>
                    {adding ? 'ADDING…' : added ? '✓ ADDED' : 'ADD TO BASKET'}
                </button>
                ) : showQuote ? (
                <button
                    onClick={handleAddToQuote}
                    className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-4 lg:px-6 py-2 lg:py-2.5 text-[13px] lg:text-sm font-bold uppercase tracking-wider transition-all duration-200 hover:scale-[1.02] active:scale-95"
                    style={{
                        borderRadius: 'var(--style-btn-radius, 8px)',
                        backgroundColor: 'var(--surface-2)',
                        color: 'var(--color-global-link-hover, var(--color-secondary, var(--accent-primary)))',
                        border: '2px solid var(--color-secondary, var(--accent-secondary, var(--accent-primary)))',
                    }}
                >
                    {/* Document icon */}
                    <svg className="w-4 h-4 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    ADD TO QUOTE
                </button>
                ) : null}
            </div>
        </div>
    ) : null;

    if (hidePurchase) return null;
    return (
        <>
            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes stickySlideDown {
                    from { transform: translateY(-100%); opacity: 0; }
                    to   { transform: translateY(0);     opacity: 1; }
                }
                @keyframes stickySlideUp {
                    from { transform: translateY(100%); opacity: 0; }
                    to   { transform: translateY(0);     opacity: 1; }
                }
                .sticky-buy-bar-enter { animation: stickySlideUp 0.3s ease-out forwards; }
                @media (min-width: 1024px) {
                    .sticky-buy-bar-enter { animation: stickySlideDown 0.3s ease-out forwards; }
                }
            `}} />

            {isVisible && typeof document !== 'undefined'
                ? createPortal(stickyBarContent, document.body)
                : null}
        </>
    );
}
